/**
 * Curated map of UK food, drink, retail, petrol & banking chains
 * → their public logo.
 *
 * Legal note: displaying a brand's real logo to help users identify their
 * real-world business location is nominative fair use — the same principle
 * Google Maps, TripAdvisor and Uber Eats operate under. We only render a
 * logo when there's a high-confidence name match; independents fall back
 * to the generic icon.
 *
 * Source: Google's public favicon service
 * (`https://www.google.com/s2/favicons?domain=X&sz=128`) — free, no auth,
 * CDN-fronted, returns each brand's own icon from its canonical domain.
 *
 * Match strategy: case-insensitive word-boundary regex on the OSM `name`.
 * Order matters — longer / more specific patterns are matched first so
 * that "Tesco Express" wins over "Tesco", "Sainsbury's Local" over
 * "Sainsbury's" etc.
 */

const logoFor = (domain) =>
  `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

// [regex against OSM name, human-facing brand, canonical domain for favicon]
const BRAND_TABLE = [
  // ─── Coffee & bakeries ─────────────────────────────────
  [/^\s*greggs\b/i, "Greggs", "greggs.co.uk"],
  [/\bcosta\s+express\b/i, "Costa Express", "costa.co.uk"],
  [/^\s*costa(\s+coffee)?\b/i, "Costa Coffee", "costa.co.uk"],
  [/^\s*starbucks\b/i, "Starbucks", "starbucks.co.uk"],
  [/\bcaf[eé]\s*nero\b|\bcaffè\s*nero\b/i, "Caffè Nero", "caffenero.com"],
  [/^\s*pret(\s+a\s+manger)?\b/i, "Pret A Manger", "pret.co.uk"],
  [/^\s*paul\s+bakery\b|^\s*paul\s+(uk|café|cafe)\b/i, "Paul", "paul-uk.com"],
  [/\bpatisserie\s+valerie\b/i, "Patisserie Valerie", "patisserie-valerie.co.uk"],
  [/\bcornish\s+bakery\b/i, "The Cornish Bakery", "cornishbakery.com"],
  [/\bwarrens?\s+bakery\b/i, "Warrens Bakery", "warrensbakery.co.uk"],
  [/\bcooplands?\b/i, "Cooplands", "cooplandsbakery.co.uk"],
  [/\bpeggy\s+porschen\b/i, "Peggy Porschen", "peggyporschen.com"],
  [/\bwenzels?\b/i, "Wenzel's", "wenzelsthebakers.co.uk"],
  [/\bgail'?s\b|^\s*gails\b/i, "Gail's Bakery", "gailsbread.co.uk"],
  [/\bbagel\s*nash\b/i, "Bagel Nash", "bagelnash.co.uk"],
  [/\ble\s+pain\s+quotidien\b/i, "Le Pain Quotidien", "lepainquotidien.com"],
  [/\bcaf[eé]\s+ritazza\b/i, "Café Ritazza", "sslservice.com"],

  // ─── Fast food ─────────────────────────────────
  [/\bmc\s*donald'?s?\b/i, "McDonald's", "mcdonalds.com"],
  [/^\s*kfc\b/i, "KFC", "kfc.co.uk"],
  [/\bburger\s*king\b/i, "Burger King", "burgerking.co.uk"],
  [/^\s*subway\b/i, "Subway", "subway.com"],
  [/\bdomino'?s\b/i, "Domino's Pizza", "dominos.co.uk"],
  [/\bpapa\s+john'?s\b/i, "Papa John's", "papajohns.co.uk"],
  [/\bpizza\s*hut\b/i, "Pizza Hut", "pizzahut.co.uk"],
  [/^\s*five\s+guys\b/i, "Five Guys", "fiveguys.co.uk"],
  [/^\s*leon\b/i, "Leon", "leon.co"],
  [/^\s*itsu\b/i, "Itsu", "itsu.com"],
  [/\bnando'?s\b/i, "Nando's", "nandos.co.uk"],
  [/^\s*wagamama\b/i, "Wagamama", "wagamama.com"],
  [/\bchopstix\b/i, "Chopstix Noodle Bar", "chopstixgroup.com"],
  [/\bwasabi\b/i, "Wasabi", "wasabi.uk.com"],
  [/\bfranco\s+manca\b/i, "Franco Manca", "francomanca.co.uk"],
  [/\brosa'?s\s+thai\b/i, "Rosa's Thai", "rosasthai.com"],
  [/\bdishoom\b/i, "Dishoom", "dishoom.com"],
  [/\byo!?\s*sushi\b/i, "YO! Sushi", "yosushi.com"],
  [/\bturtle\s+bay\b/i, "Turtle Bay", "turtlebay.co.uk"],
  [/\bgbk\b|\bgourmet\s+burger\s+kitchen\b/i, "GBK", "gbk.co.uk"],
  [/\btortilla\b/i, "Tortilla", "tortilla.co.uk"],
  [/\bchipotle\b/i, "Chipotle", "chipotle.co.uk"],

  // ─── Casual / mid dining ─────────────────────────────────
  [/\bpizza\s+express\b/i, "Pizza Express", "pizzaexpress.com"],
  [/\bzizzi\b/i, "Zizzi", "zizzi.co.uk"],
  [/\bprezzo\b/i, "Prezzo", "prezzorestaurants.co.uk"],
  [/\bask\s+italian\b/i, "ASK Italian", "askitalian.co.uk"],
  [/\bstrada\b/i, "Strada", "strada.co.uk"],
  [/\bcaf[eé]\s+rouge\b/i, "Café Rouge", "caferouge.com"],
  [/\bbella\s+italia\b/i, "Bella Italia", "bellaitalia.co.uk"],
  [/\bcarluccio'?s\b/i, "Carluccio's", "carluccios.com"],
  [/\bloch\s+fyne\b/i, "Loch Fyne", "lochfyne.com"],
  [/\btgi\s+friday'?s\b|\bT\.?G\.?I\.?\s*Friday'?s\b/i, "TGI Fridays", "tgifridays.co.uk"],
  [/\bthe\s+ivy\b/i, "The Ivy", "theivycollection.com"],
  [/\bbill'?s\b/i, "Bill's Restaurant", "bills-website.co.uk"],
  [/\bgiraffe\b/i, "Giraffe", "giraffe.net"],
  [/\bcote\b|\bc[oô]te\s+brasserie\b/i, "Côte Brasserie", "cote.co.uk"],
  [/\bchiquito\b/i, "Chiquito", "chiquito.co.uk"],
  [/\blas\s+iguanas\b/i, "Las Iguanas", "iguanas.co.uk"],

  // ─── Pubs & pub-grub chains ─────────────────────────────────
  [/\bwetherspoons?\b|\bJ\.?D\.?\s*Wetherspoon/i, "Wetherspoons", "jdwetherspoon.com"],
  [/\btoby\s+carvery\b/i, "Toby Carvery", "tobycarvery.co.uk"],
  [/\bharvester\b/i, "Harvester", "harvester.co.uk"],
  [/\bbeefeater\b/i, "Beefeater", "beefeater.co.uk"],
  [/\bmiller\s*(&|and)\s*carter\b/i, "Miller & Carter", "millerandcarter.co.uk"],
  [/\bfrankie\s*(&|and)\s*benny'?s\b/i, "Frankie & Benny's", "frankieandbennys.com"],
  [/\bmarston'?s\b/i, "Marston's", "marstons.co.uk"],
  [/\bgreene\s+king\b/i, "Greene King", "greeneking.co.uk"],
  [/\bfullers?\b|\bfuller'?s\b/i, "Fuller's", "fullers.co.uk"],
  [/\bhungry\s+horse\b/i, "Hungry Horse", "hungryhorse.co.uk"],
  [/\bstonehouse\b/i, "Stonehouse", "stonehouserestaurants.co.uk"],
  [/\bember\s+inns?\b/i, "Ember Inns", "emberinns.co.uk"],
  [/\bcrown\s+carveries\b/i, "Crown Carveries", "crowncarveries.co.uk"],
  [/\bwhitbread\b/i, "Whitbread", "whitbread.co.uk"],
  [/\ball\s+bar\s+one\b/i, "All Bar One", "allbarone.co.uk"],
  [/\bsizzling\s+pubs?\b/i, "Sizzling Pubs", "sizzlingpubs.co.uk"],
  [/\bvintage\s+inns?\b/i, "Vintage Inns", "vintageinn.co.uk"],
  [/\bfarmhouse\s+inns?\b/i, "Farmhouse Inns", "farmhouseinns.co.uk"],
  [/\bchef\s*(&|and)\s*brewer\b/i, "Chef & Brewer", "chefandbrewer.com"],
  [/\bslug\s*(&|and)\s*lettuce\b/i, "Slug and Lettuce", "slugandlettuce.co.uk"],
  [/\brevolution(\s+bar)?\b/i, "Revolution", "revolution-bars.co.uk"],
  [/\bpremier\s+inn\b/i, "Premier Inn", "premierinn.com"],

  // ─── Supermarkets — MAJOR (order: specific → general) ─────
  // Tesco variants first
  [/\btesco\s+extra\b/i, "Tesco Extra", "tesco.com"],
  [/\btesco\s+express\b/i, "Tesco Express", "tesco.com"],
  [/\btesco\s+metro\b/i, "Tesco Metro", "tesco.com"],
  [/\btesco\s+superstore\b/i, "Tesco Superstore", "tesco.com"],
  [/\btesco\s+petrol\b/i, "Tesco Petrol", "tesco.com"],
  [/^\s*tesco\b/i, "Tesco", "tesco.com"],
  // Sainsbury's
  [/\bsainsbury'?s?\s+local\b/i, "Sainsbury's Local", "sainsburys.co.uk"],
  [/\bsainsbury'?s?\s+petrol\b/i, "Sainsbury's Petrol", "sainsburys.co.uk"],
  [/\bsainsbury'?s?\b/i, "Sainsbury's", "sainsburys.co.uk"],
  // Asda
  [/\basda\s+express\b/i, "Asda Express", "asda.com"],
  [/\basda\s+petrol\b/i, "Asda Petrol", "asda.com"],
  [/\basda\b/i, "Asda", "asda.com"],
  // Morrisons
  [/\bmorrisons?\s+daily\b/i, "Morrisons Daily", "morrisons.com"],
  [/\bmorrisons?\s+petrol\b/i, "Morrisons Petrol", "morrisons.com"],
  [/\bmorrisons?\b/i, "Morrisons", "morrisons.com"],
  // Waitrose
  [/\blittle\s+waitrose\b/i, "Little Waitrose", "waitrose.com"],
  [/\bwaitrose\b/i, "Waitrose", "waitrose.com"],
  // M&S
  [/\bm\s*&\s*s\s+food\b|\bmarks?\s*(&|and)\s+spencer\s+(food|simply)\b/i, "M&S Food", "marksandspencer.com"],
  [/\bmarks?\s*(&|and)\s+spencer\b|\bm\s*&\s*s\b/i, "M&S", "marksandspencer.com"],
  // Discount grocers
  [/^\s*aldi\b/i, "Aldi", "aldi.co.uk"],
  [/^\s*lidl\b/i, "Lidl", "lidl.co.uk"],
  [/\biceland\s+foods?\b|^\s*iceland\b/i, "Iceland", "iceland.co.uk"],
  [/\bfarmfoods\b/i, "Farmfoods", "farmfoods.co.uk"],
  [/\bwhole\s*foods?(\s+market)?\b/i, "Whole Foods Market", "wholefoodsmarket.com"],
  [/\bocado\b/i, "Ocado", "ocado.com"],
  // Co-op & multi-brand convenience
  [/\bco[- ]?op(erative)?(\s+food)?\b/i, "Co-op", "coop.co.uk"],
  [/^\s*spar\b/i, "Spar", "spar.co.uk"],
  [/\bnisa\s+local\b|^\s*nisa\b/i, "Nisa", "nisalocally.co.uk"],
  [/\bcostcutter\b/i, "Costcutter", "costcutter.co.uk"],
  [/\bbudgens\b/i, "Budgens", "budgens.co.uk"],
  [/\blondis\b/i, "Londis", "londis.co.uk"],
  [/^\s*premier\s+(store|shop)\b/i, "Premier", "premier-stores.co.uk"],
  [/\bmc\s*coll'?s\b/i, "McColl's", "mccolls.co.uk"],
  [/\bone\s+stop\b/i, "One Stop", "onestop.co.uk"],
  [/\bbargain\s+booze\b/i, "Bargain Booze", "bargainbooze.co.uk"],
  [/\bfamily\s+shopper\b/i, "Family Shopper", "familyshopper.co.uk"],

  // ─── High-street retail ─────────────────────────────────
  [/\bwh\s*smith\b|\bw\.?h\.?\s*smith\b/i, "WHSmith", "whsmith.co.uk"],
  [/\bboots\s+pharmacy\b|^\s*boots\b/i, "Boots", "boots.com"],
  [/\bsuperdrug\b/i, "Superdrug", "superdrug.com"],
  [/\bpoundland\b/i, "Poundland", "poundland.co.uk"],
  [/\bb\s*&\s*m\b|\bb\s*and\s*m\b/i, "B&M", "bmstores.co.uk"],
  [/\bhome\s+bargains\b/i, "Home Bargains", "homebargains.co.uk"],
  [/\bwilko\b/i, "Wilko", "wilko.com"],
  [/\bpoundstretcher\b/i, "Poundstretcher", "poundstretcher.co.uk"],
  [/\bsavers\b/i, "Savers", "savers.co.uk"],
  [/\bthe\s+range\b/i, "The Range", "therange.co.uk"],
  [/\bargos\b/i, "Argos", "argos.co.uk"],
  [/\bcurrys\b/i, "Currys", "currys.co.uk"],
  [/\bjohn\s+lewis\b/i, "John Lewis", "johnlewis.com"],
  [/^\s*next\b/i, "Next", "next.co.uk"],
  [/\bprimark\b/i, "Primark", "primark.com"],
  [/\btk\s+maxx\b/i, "TK Maxx", "tkmaxx.com"],
  [/\bh\s*&\s*m\b/i, "H&M", "hm.com"],
  [/^\s*zara\b/i, "Zara", "zara.com"],
  [/\bsports\s+direct\b/i, "Sports Direct", "sportsdirect.com"],
  [/\bjd\s+sports\b/i, "JD Sports", "jdsports.co.uk"],
  [/\bdecathlon\b/i, "Decathlon", "decathlon.co.uk"],
  [/\bblacks(\s+outdoor)?\b/i, "Blacks", "blacks.co.uk"],
  [/\bcotswold\s+outdoor\b/i, "Cotswold Outdoor", "cotswoldoutdoor.com"],
  [/\bmillets\b/i, "Millets", "millets.co.uk"],
  [/\bgo\s+outdoors\b/i, "GO Outdoors", "gooutdoors.co.uk"],
  [/\bmountain\s+warehouse\b/i, "Mountain Warehouse", "mountainwarehouse.com"],
  [/\btrespass\b/i, "Trespass", "trespass.com"],
  [/\bwaterstones?\b/i, "Waterstones", "waterstones.com"],
  [/\bryman\b/i, "Ryman", "ryman.co.uk"],
  [/\bpaperchase\b/i, "Paperchase", "paperchase.com"],
  [/\bthe\s+works\b/i, "The Works", "theworks.co.uk"],
  [/\bhmv\b/i, "HMV", "hmv.com"],
  [/^\s*game\s+(store|shop|uk)\b|^\s*game\b(?!\s*(?:reserve|fair))/i, "GAME", "game.co.uk"],

  // ─── Petrol / fuel ─────────────────────────────────
  [/^\s*bp\b|\bbritish\s+petroleum\b/i, "BP", "bp.com"],
  [/^\s*shell\b(?!\s*station\s+bay)/i, "Shell", "shell.co.uk"],
  [/^\s*esso\b/i, "Esso", "esso.co.uk"],
  [/^\s*texaco\b/i, "Texaco", "texaco.co.uk"],
  [/^\s*jet\b(?!\s+ski)/i, "Jet", "jetlocal.co.uk"],
  [/^\s*gulf\b(?!\s+of)/i, "Gulf", "gulfretail.co.uk"],
  [/\bapplegreen\b/i, "Applegreen", "applegreenstores.com"],
  [/\bmurco\b/i, "Murco", "murco.co.uk"],
  [/\beg\s+group\b/i, "EG Group", "eg.group"],
  [/\bmoto\s+services?\b/i, "Moto", "moto-way.com"],
  [/\bwelcome\s+break\b/i, "Welcome Break", "welcomebreak.co.uk"],
  [/\broadchef\b/i, "Roadchef", "roadchef.com"],

  // ─── Banks & financial ─────────────────────────────────
  [/\bbarclays\b/i, "Barclays", "barclays.co.uk"],
  [/\bhsbc\b/i, "HSBC", "hsbc.co.uk"],
  [/\bnatwest\b/i, "NatWest", "natwest.com"],
  [/\bhalifax\b/i, "Halifax", "halifax.co.uk"],
  [/\blloyds(\s+bank)?\b/i, "Lloyds Bank", "lloydsbank.com"],
  [/\bsantander\b/i, "Santander", "santander.co.uk"],
  [/\bnationwide(\s+building\s+society)?\b/i, "Nationwide", "nationwide.co.uk"],
  [/\btsb\b/i, "TSB", "tsb.co.uk"],
  [/\bmetro\s+bank\b/i, "Metro Bank", "metrobankonline.co.uk"],
  [/\bpost\s+office\b/i, "Post Office", "postoffice.co.uk"],
  [/\broyal\s+bank\s+of\s+scotland\b|\brbs\b/i, "Royal Bank of Scotland", "rbs.co.uk"],
  [/\bbank\s+of\s+scotland\b/i, "Bank of Scotland", "bankofscotland.co.uk"],
  [/\byorkshire\s+bank\b/i, "Yorkshire Bank", "virginmoney.com"],
  [/\bvirgin\s+money\b/i, "Virgin Money", "virginmoney.com"],
  [/\bmonzo\b/i, "Monzo", "monzo.com"],
  [/\bstarling\b/i, "Starling Bank", "starlingbank.com"],

  // ─── Pharmacies (chemists) ─────────────────────────────────
  [/\blloyds?pharmacy\b/i, "LloydsPharmacy", "lloydspharmacy.com"],
  [/\bwell\s+pharmacy\b/i, "Well Pharmacy", "well.co.uk"],
  [/\browlands\s+pharmacy\b/i, "Rowlands Pharmacy", "rowlandspharmacy.co.uk"],
];

/**
 * Match a POI name to a known brand.
 * @param {string} name — OSM name field
 * @returns {{ brand: string, logoUrl: string } | null}
 */
export function matchBrandLogo(name) {
  if (!name || typeof name !== "string") return null;
  for (const [rx, brand, domain] of BRAND_TABLE) {
    if (rx.test(name)) {
      return { brand, logoUrl: logoFor(domain) };
    }
  }
  return null;
}
